/**
 * Connection lifecycle tools — create, test, fix, and delete the connections
 * flows authenticate through. Completes the story where build_flow previously
 * had to stop and send the user to the portal.
 *
 * The server is headless: OAuth consent can't happen here, so create/fix hand
 * the user a place to sign in — a direct consent link where the environment
 * exposes one, otherwise a maker-portal deep link. Connected metadata alone
 * does not prove renewed consent, granted scopes, or runtime access.
 */

import { z } from "zod";
import {
  ConsentLinkUnavailableError,
  isConnected,
  makerPortalConnectionUrl,
  type ConnectionsApi,
  type ConnectivityConnection,
} from "../api/connections-api.js";
import { logger } from "../utils/logger.js";
import { ConfirmationRequiredError, ValidationError } from "../utils/errors.js";
import { projectConnectionMetadata } from "./connection-metadata.js";

const CONNECTOR_DESCRIPTION =
  "Connector API name, e.g. shared_sharepointonline, shared_office365, shared_teams (find it via search_connectors or list_connections)";
const CONNECTION_DESCRIPTION = "The connection's name (GUID-like ID from list_connections or create_connection)";

// =============================================================================
// Tool definitions
// =============================================================================

export const createConnectionTool = {
  name: "create_connection",
  description:
    "Create a connection for a connector. OAuth connectors (SharePoint, Office 365, Teams…) are created unauthenticated and come back with sign-in instructions — a direct consent link where the environment offers one, otherwise a deep link to the connection in the maker portal — then test_connection confirms. API-key style connectors accept their values via parameters. Connectors publishing several authentication modes (Azure Key Vault, SQL Server, Azure Blob…) need parameterSet; omitting it is refused with the valid modes rather than creating a connection that can never be signed in.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connector: { type: "string", description: CONNECTOR_DESCRIPTION },
      environment: {
        type: "string",
        description: "Environment GUID. Uses default if omitted.",
      },
      displayName: { type: "string", description: "Optional display name for the connection" },
      parameters: {
        type: "object",
        description:
          "Connector-specific connection parameters (API keys, instance URLs…). OAuth connectors usually need none — consent happens in the browser.",
      },
      parameterSet: {
        type: "string",
        description:
          "Authentication mode, for connectors that publish more than one (e.g. Azure Key Vault: oauthDefault, oauthServicePrincipal, CertOauth). Required for those connectors — the error names the valid values.",
      },
    },
    required: ["connector"],
  },
};

export const testConnectionTool = {
  name: "test_connection",
  description:
    "Read a connection's Connected/error metadata — use after create_connection consent, or when a flow fails with connection/auth errors. Returns bounded structured identity, timestamps, status labels and test-link count only; missing metadata is explicit. This does not execute the connector or verify granted scopes, renewed consent, or runtime access.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connector: { type: "string", description: CONNECTOR_DESCRIPTION },
      connectionName: { type: "string", description: CONNECTION_DESCRIPTION },
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
    },
    required: ["connector", "connectionName"],
  },
};

export const fixConnectionTool = {
  name: "fix_connection",
  description:
    "Get a user sign-in handoff for an existing connection. Defaults to no-op when metadata reports Connected; set reauthorize=true to request a fresh consent link even then, for example after connector scopes change. Verifies the existing connection ID, connector and environment before requesting the link; never recreates it, changes grants, or completes sign-in automatically. Only an explicit unavailable-route response falls back to its maker-portal link. A link or Connected metadata does not prove renewed consent, granted scopes, or runtime access.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connector: { type: "string", description: CONNECTOR_DESCRIPTION },
      connectionName: { type: "string", description: CONNECTION_DESCRIPTION },
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
      reauthorize: {
        type: "boolean",
        description:
          "Explicitly request a fresh user sign-in handoff even when this existing connection reports Connected. Default false; does not grant permissions or complete sign-in.",
      },
    },
    required: ["connector", "connectionName"],
  },
};

export const deleteConnectionTool = {
  name: "delete_connection",
  description:
    "Permanently delete a connection. Flows bound to it will fail until re-bound. Requires confirm=true.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connector: { type: "string", description: CONNECTOR_DESCRIPTION },
      connectionName: { type: "string", description: CONNECTION_DESCRIPTION },
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
      confirm: { type: "boolean", description: "Set true to confirm permanent deletion" },
    },
    required: ["connector", "connectionName"],
  },
};

export const ensureConnectionTool = {
  name: "ensure_connection",
  description:
    "Get a working connection for a connector in one call: returns an existing Connected one if there is any, otherwise creates one and hands back sign-in instructions. Use this instead of stitching list/create/test together — it is the right first step before binding a connector into a flow.",
  inputSchema: {
    type: "object" as const,
    properties: {
      connector: { type: "string", description: CONNECTOR_DESCRIPTION },
      environment: { type: "string", description: "Environment GUID. Uses default if omitted." },
      create: {
        type: "boolean",
        description:
          "Create a connection when no usable one exists (default true). Set false to only look.",
      },
    },
    required: ["connector"],
  },
};

export const connectionLifecycleTools = [
  ensureConnectionTool,
  createConnectionTool,
  testConnectionTool,
  fixConnectionTool,
  deleteConnectionTool,
];

// =============================================================================
// Input schemas
// =============================================================================

const createSchema = z.object({
  connector: z.string().min(3),
  environment: z.string().optional(),
  displayName: z.string().min(1).max(200).optional(),
  parameters: z.record(z.string(), z.unknown()).optional(),
  parameterSet: z.string().min(1).optional(),
});

const connectionRefSchema = z.object({
  connector: z.string().min(3),
  connectionName: z.string().min(3),
  environment: z.string().optional(),
});

const fixSchema = connectionRefSchema
  .extend({
    connector: z.string().regex(/^[a-z0-9][a-z0-9_-]{2,80}$/i),
    connectionName: z.string().regex(/^[a-z0-9][a-z0-9-]{2,80}$/i),
    environment: z
      .string()
      .regex(/^(?:Default-)?[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i)
      .optional(),
    reauthorize: z.boolean().optional().default(false),
  })
  .strict();

const deleteSchema = connectionRefSchema.extend({
  confirm: z.boolean().optional().default(false),
});

const ensureSchema = z.object({
  connector: z.string().min(3),
  environment: z.string().optional(),
  create: z.boolean().optional().default(true),
});

// =============================================================================
// Formatting
// =============================================================================

function describeStatuses(connection: ConnectivityConnection): string {
  const statuses = connection.properties?.statuses ?? [];
  if (statuses.length === 0) return "no status reported";
  return statuses
    .map((s) => {
      const error = s.error ? ` (${s.error.code ?? "error"}: ${s.error.message ?? "no message"})` : "";
      return `${s.status ?? "Unknown"}${error}`;
    })
    .join(", ");
}

function consentInstructions(consentLink: string): string[] {
  return [
    "To finish authentication:",
    `  1. Open this link in a browser: ${consentLink}`,
    "  2. Sign in with the account the connection should act as.",
    "  3. Run test_connection to read the connection status, then verify a permitted benign operation that needs the required scopes. Connected metadata alone does not prove renewed consent or runtime access.",
  ];
}

/**
 * Fallback when the environment exposes no consent-link route: send the user
 * to the connection in the maker portal, where "Fix connection" completes the
 * same OAuth handshake.
 */
function portalInstructions(
  environmentId: string,
  connector: string,
  connectionName: string,
): string[] {
  return [
    "This environment doesn't expose a direct consent link, so finish authentication in the portal:",
    `  1. Open ${makerPortalConnectionUrl(environmentId, connector, connectionName)}`,
    '  2. Select the connection and choose "Fix connection" (or the sign-in prompt), then sign in.',
    "  3. Run test_connection to read status, then verify a permitted benign operation requiring the expected scopes. Connected metadata alone does not prove renewed consent or runtime access.",
  ];
}

// =============================================================================
// Handlers
// =============================================================================

export async function handleCreateConnection(api: ConnectionsApi, input: unknown): Promise<string> {
  const parsed = createSchema.parse(input);
  logger.info({ connector: parsed.connector }, "Executing create_connection");
  const result = await api.createConnection({
    connector: parsed.connector,
    environmentId: parsed.environment,
    displayName: parsed.displayName,
    parameters: parsed.parameters,
    parameterSet: parsed.parameterSet,
  });

  const lines = [
    `Connection created: ${result.connectionName} (connector ${parsed.connector}).`,
  ];
  if (result.connected) {
    lines.push("Status: Connected — ready to use in flows and connection references.");
    return lines.join("\n");
  }
  if (result.consent?.consentLink) {
    lines.push(`Status: ${describeStatuses(result.connection)}.`, "", ...consentInstructions(result.consent.consentLink));
    return lines.join("\n");
  }
  lines.push(
    `Status: ${describeStatuses(result.connection)}.`,
    "",
    ...portalInstructions(result.environmentId, parsed.connector, result.connectionName),
  );
  return lines.join("\n");
}

export async function handleTestConnection(api: ConnectionsApi, input: unknown) {
  const parsed = connectionRefSchema.extend({
    connector: z.string().trim().regex(/^[a-z0-9][a-z0-9_-]{1,80}$/i),
    connectionName: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{1,80}$/i),
  }).parse(input);
  const environment = api.resolveEnvironmentId(parsed.environment);
  if (!/^(?:Default-)?[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(environment)) {
    throw new ValidationError("Connection metadata requires a valid resolved environment ID.");
  }
  logger.info(
    { connector: parsed.connector, connection: parsed.connectionName },
    "Executing test_connection",
  );
  const connection = await api.getConnection(
    parsed.connector,
    parsed.connectionName,
    parsed.environment,
  );
  const projection = projectConnectionMetadata(connection, { ...parsed, environment });
  const statuses = projection.metadata.statuses;
  const limits = "This does not verify renewed consent, granted scopes, or runtime access.";
  let text: string;
  if (statuses.mixed) {
    text = `Connection ${parsed.connectionName} reports mixed${statuses.incomplete ? " and incomplete" : ""} status metadata: ${projection.statusDescription}. ${limits}`;
  } else if (statuses.incomplete) {
    text = `Connection ${parsed.connectionName} has incomplete or unavailable status metadata: ${projection.statusDescription}. ${limits}`;
  } else if (statuses.reportedConnected) {
    text = `Connection ${parsed.connectionName} reports Connected metadata. ${limits}`;
  } else {
    text = [
      `Connection ${parsed.connectionName} is NOT healthy: ${projection.statusDescription}.`,
      "Run fix_connection for re-authentication instructions.",
    ].join("\n");
  }
  return { text, structured: { connectionMetadata: projection.metadata } };
}

export async function handleFixConnection(api: ConnectionsApi, input: unknown): Promise<string> {
  const parsed = fixSchema.parse(input);
  logger.info(
    { connector: parsed.connector, connection: parsed.connectionName },
    "Executing fix_connection",
  );
  const connection = await api.getConnection(
    parsed.connector,
    parsed.connectionName,
    parsed.environment,
  );
  if (isConnected(connection) && !parsed.reauthorize) {
    return `Connection ${parsed.connectionName} is already Connected according to metadata; no consent request was sent. This does not verify granted scopes or runtime access. Set reauthorize=true to request a fresh user sign-in handoff for this existing connection.`;
  }
  const status = isConnected(connection) ? "Connected" : "not Connected";
  let consent;
  try {
    consent = await api.getConsentLink(parsed.connector, parsed.connectionName, parsed.environment);
  } catch (error) {
    if (error instanceof ConsentLinkUnavailableError) {
      return [
        `Connection ${parsed.connectionName} reports ${status} metadata. The service reported that its consent-link route is unavailable.`,
        "No reauthorization was completed and the existing connection was not replaced. Use the existing connection account when signing in.",
        "",
        ...portalInstructions(
          api.resolveEnvironmentId(parsed.environment),
          parsed.connector,
          parsed.connectionName,
        ),
      ].join("\n");
    }
    throw error;
  }
  if (consent.consentLink) {
    return [
      `User sign-in handoff generated for existing connection ${parsed.connectionName} (${status} metadata).`,
      "Reauthorization and granted scopes are not yet verified. Sign in with this connection's existing account; no connection, client-secret, or permission settings were changed by this tool.",
      "",
      ...consentInstructions(consent.consentLink),
    ].join("\n");
  }
  throw new ValidationError(
    "The service returned no user sign-in link. No reauthorization was completed and the existing connection was not replaced.",
  );
}

export async function handleDeleteConnection(api: ConnectionsApi, input: unknown): Promise<string> {
  const parsed = deleteSchema.parse(input);
  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Confirm permanent deletion of connection ${parsed.connectionName} (connector ${parsed.connector})? Flows bound to it will fail until re-bound. Set confirm=true to proceed.`);
  }
  logger.info(
    { connector: parsed.connector, connection: parsed.connectionName },
    "Executing delete_connection",
  );
  await api.deleteConnection(parsed.connector, parsed.connectionName, parsed.environment);
  return `Connection ${parsed.connectionName} permanently deleted.`;
}

/**
 * Idempotent "give me a usable connection" entry point.
 *
 * Resolution order: an existing Connected connection wins; otherwise an
 * existing unauthenticated one is reported (creating another would just add
 * clutter the user still has to authorize); otherwise create one.
 */
export async function handleEnsureConnection(api: ConnectionsApi, input: unknown): Promise<string> {
  const parsed = ensureSchema.parse(input);
  logger.info({ connector: parsed.connector }, "Executing ensure_connection");
  const envId = api.resolveEnvironmentId(parsed.environment);

  const existing = await api.listConnections({
    connector: parsed.connector,
    environmentId: parsed.environment,
  });
  const connected = existing.find((c) => isConnected(c));
  if (connected?.name) {
    return [
      `Ready: connection ${connected.name} (${parsed.connector}) is Connected.`,
      "Use this connection name when binding the connector into a flow.",
    ].join("\n");
  }

  const unauthenticated = existing.find((c) => c.name);
  if (unauthenticated?.name) {
    return [
      `Found an existing ${parsed.connector} connection that is not usable yet: ${unauthenticated.name} — ${describeStatuses(unauthenticated)}.`,
      "",
      ...portalInstructions(envId, parsed.connector, unauthenticated.name),
      "",
      "Authorizing this one is better than creating another; run fix_connection for the same instructions later.",
    ].join("\n");
  }

  if (!parsed.create) {
    return `No ${parsed.connector} connection exists in this environment, and create was set to false. Run create_connection (or call again with create: true) to make one.`;
  }

  return [
    `No existing ${parsed.connector} connection — creating one.`,
    "",
    await handleCreateConnection(api, {
      connector: parsed.connector,
      environment: parsed.environment,
    }),
  ].join("\n");
}

export const connectionLifecycleHandlers = {
  ensure_connection: handleEnsureConnection,
  create_connection: handleCreateConnection,
  test_connection: handleTestConnection,
  fix_connection: handleFixConnection,
  delete_connection: handleDeleteConnection,
} as const;
